import { useEffect, useRef, useState } from 'react';

export const prefersReducedMotion = () =>
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const canHover = () =>
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/**
 * Adds `is-visible` to every `[data-reveal]` element once it enters the viewport.
 * Re-scans when `deps` change so content rendered later is picked up too.
 */
export function useRevealOnScroll(deps = []) {
    useEffect(() => {
        const nodes = Array.from(document.querySelectorAll('[data-reveal]:not(.is-visible)'));
        if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
            nodes.forEach((node) => node.classList.add('is-visible'));
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
        );
        nodes.forEach((node) => observer.observe(node));
        return () => observer.disconnect();
    }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Pointer-driven 3D tilt + spotlight. Writes CSS variables consumed by `.tilt`.
 */
export function useTilt(max = 8) {
    const ref = useRef(null);

    useEffect(() => {
        const node = ref.current;
        if (!node || !canHover() || prefersReducedMotion()) return undefined;

        let frame = 0;
        const handleMove = (event) => {
            const rect = node.getBoundingClientRect();
            const x = (event.clientX - rect.left) / rect.width;
            const y = (event.clientY - rect.top) / rect.height;
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                node.style.setProperty('--rx', `${(0.5 - y) * max}deg`);
                node.style.setProperty('--ry', `${(x - 0.5) * max}deg`);
                node.style.setProperty('--mx', `${x * 100}%`);
                node.style.setProperty('--my', `${y * 100}%`);
            });
        };
        const handleLeave = () => {
            cancelAnimationFrame(frame);
            node.style.setProperty('--rx', '0deg');
            node.style.setProperty('--ry', '0deg');
        };

        node.addEventListener('pointermove', handleMove);
        node.addEventListener('pointerleave', handleLeave);
        return () => {
            cancelAnimationFrame(frame);
            node.removeEventListener('pointermove', handleMove);
            node.removeEventListener('pointerleave', handleLeave);
        };
    }, [max]);

    return ref;
}

/**
 * Makes an element drift slightly towards the pointer while hovered.
 */
export function useMagnetic(strength = 0.25) {
    const ref = useRef(null);

    useEffect(() => {
        const node = ref.current;
        if (!node || !canHover() || prefersReducedMotion()) return undefined;

        const handleMove = (event) => {
            const rect = node.getBoundingClientRect();
            const x = event.clientX - (rect.left + rect.width / 2);
            const y = event.clientY - (rect.top + rect.height / 2);
            node.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
        };
        const handleLeave = () => {
            node.style.transform = '';
        };

        node.addEventListener('pointermove', handleMove);
        node.addEventListener('pointerleave', handleLeave);
        return () => {
            node.removeEventListener('pointermove', handleMove);
            node.removeEventListener('pointerleave', handleLeave);
        };
    }, [strength]);

    return ref;
}

/**
 * Returns how far (0 → 1) an element has travelled through the viewport.
 */
export function useScrollProgress() {
    const ref = useRef(null);
    const [progress, setProgress] = useState(prefersReducedMotion() ? 1 : 0);

    useEffect(() => {
        if (prefersReducedMotion()) return undefined;
        let frame = 0;
        const update = () => {
            const node = ref.current;
            if (!node) return;
            const rect = node.getBoundingClientRect();
            const viewport = window.innerHeight || 1;
            const start = viewport * 0.85;
            const end = viewport * 0.35;
            const value = (start - rect.top) / (start - end + rect.height * 0.6);
            setProgress(Math.min(1, Math.max(0, value)));
        };
        const onScroll = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(update);
        };
        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, []);

    return [ref, progress];
}
